/**
 * WelcomeTour — MERCHANT-ONBOARDING-REVIEW-1 add-on
 *
 * A friendly first-login walkthrough shown to merchants who have just
 * signed in and have not yet seen it. Three lightweight steps highlighting
 *   Overview → Setup Guide → Review before Publish
 * so a new merchant never feels lost.
 *
 * Dismissal is per-user via localStorage — never mutates server state.
 * If the merchant clicks "Take me there" on step 2 we route them straight
 * into the Storefront Setup Guide.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Store, Rocket, ArrowRight, ArrowLeft, X, CheckCircle2,
} from "lucide-react";

const DISMISS_KEY = "cy_merchant_welcome_tour_v1";

const STEPS = [
  {
    id: "overview",
    icon: Sparkles,
    kicker: "Welcome to Cyannect",
    title: "This is your Merchant home base.",
    body:
      "See performance, redemption activity, and readiness alerts at a glance. When something needs attention, we'll surface it here first.",
    cta: "Show me the Setup Guide",
  },
  {
    id: "setup-guide",
    icon: Store,
    kicker: "Storefront first",
    title: "Set up your Storefront in six guided steps.",
    body:
      "Business identity, shopper destination, Province Coverage, categories, integration readiness — then a Review Storefront summary. Your draft is saved automatically so you can pick up where you left off.",
    cta: "Take me to Setup Guide",
    href: "/merchant/storefront-setup",
  },
  {
    id: "review-publish",
    icon: Rocket,
    kicker: "Review before publish",
    title: "Every new Offer gets a self-review step.",
    body:
      "You create a Draft, we run all the checks server-side, and you Publish only when everything is green. No admin queue in between for the normal flow.",
    cta: "Got it — let's go",
  },
];


export default function WelcomeTour() {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(true); // hidden until we know
  const [i, setI] = useState(0);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(DISMISS_KEY);
      setDismissed(!!seen);
    } catch {
      setDismissed(false);
    }
  }, []);

  const close = () => {
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch {}
    setDismissed(true);
  };

  const step = STEPS[i];
  const StepIcon = step?.icon || Sparkles;
  const isLast = i === STEPS.length - 1;
  const next = () => {
    if (step.href && !isLast) {
      close();
      navigate(step.href);
      return;
    }
    if (isLast) { close(); return; }
    setI((n) => Math.min(STEPS.length - 1, n + 1));
  };

  const dotStyle = useMemo(
    () => STEPS.map((_, idx) => idx === i),
    [i],
  );

  if (dismissed) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 p-4"
         onClick={close}
         data-testid="welcome-tour-backdrop">
      <div className="relative w-full max-w-lg rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200"
           onClick={(e) => e.stopPropagation()}
           data-testid="welcome-tour-modal">
        <button onClick={close}
                className="absolute top-3 right-3 rounded-full p-1.5 text-slate-500 hover:bg-slate-100"
                aria-label="Dismiss welcome tour"
                data-testid="welcome-tour-close">
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 md:p-8">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-cyan-700">
            <StepIcon className="w-4 h-4" /> {step.kicker}
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mt-2">
            {step.title}
          </h3>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed">
            {step.body}
          </p>

          <div className="mt-6 flex items-center justify-between gap-3">
            <div className="flex gap-1.5" data-testid="welcome-tour-dots">
              {dotStyle.map((active, idx) => (
                <span key={idx}
                      className={`w-1.5 h-1.5 rounded-full ${active ? "bg-cyan-600" : "bg-slate-300"}`} />
              ))}
            </div>
            <div className="flex gap-2">
              {i > 0 && (
                <button onClick={() => setI((n) => Math.max(0, n - 1))}
                        className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                        data-testid="welcome-tour-back">
                  <ArrowLeft className="w-3 h-3" /> Back
                </button>
              )}
              <button onClick={next}
                      className="pf-btn-primary px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                      data-testid="welcome-tour-next">
                {isLast ? (<><CheckCircle2 className="w-3.5 h-3.5" /> {step.cta}</>)
                        : (<>{step.cta} <ArrowRight className="w-3 h-3" /></>)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
